import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  fetchCreditApplicationSignatureSettings,
  getCreditApplication,
  updateCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { getCreditApplicationTemplate } from '../../../../redux/services/creditApplicationsTemplatesService';
import { PreviewCreditApplicationData } from '../CreditApplicationComponents/PreviewCreditApplicationData';
import CreateSignatureModal from '../../lienWaivers/supplier/CreateSignatureModal/CreateSignatureModal';
import CustomerSignatureModal from '../CreditApplicationComponents/CustomerSignatureModal';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';
import JobSavingSuccess from '../../../../pages/RequestJobInformationSheet/JobSavingSuccess';
import { DynamicInvitedSignaturesFields, getCreditApplicationTypeLabel } from '@dill/dill-shared';
import CryptoJS from 'crypto-js';
import { decryptCreditApplicationData } from '../../../../utils/decryptCreditApplicationData';
import { useDecryption } from '../../../../utils/useDecryption';
import { DillButton } from '../../../general/Dill/DillButton';

enum ModalState {
  ADD_SIGNATURE,
  SUCCESS_ADDING_SIGNATURE,
}

export const ReviewAndSignCreditApplication = () => {
  const dispatch = useAppDispatch();
  const [creditApplicationId, setCreditApplicationId] = useState('');
  const [applicationHistoryId, setApplicationHistoryId] = useState<string | null>(null);
  const [isSignatureModalOpen, setSignatureModalOpen] = useState(false);
  const [isCustomerSignatureModalOpen, setIsCustomerSignatureModalOpen] = useState(false);
  const [drawnSignature, setDrawnSignature] = useState('');
  const [currentSigningType, setCurrentSigningType] = useState('');
  const [pendingSignatureData, setPendingSignatureData] = useState<
    Record<
      string,
      {
        dynamicFields: DynamicInvitedSignaturesFields[];
        signature: string;
        email: string;
        placeHolder: string;
        dateSigned: string;
        ipAddress?: string;
        location?: string;
      }
    >
  >({});
  const [submitError, setSubmitError] = useState('');
  const [signatureErrors, setSignatureErrors] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [type, setType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState(ModalState.ADD_SIGNATURE);
  const [modalDetails, setModalDetails] = useState<{
    modalType: 'Success' | 'Error';
    title: string;
    details: string;
  }>({
    modalType: 'Success',
    title: '',
    details: '',
  });
  const [ipdData, setIpData] = useState<{ ipAddress: string; location: string } | null>(null);
  const [decryptedFieldValues, setDecryptedFieldValues] = useState<any[]>([]);
  const [decryptedBankReference, setDecryptedBankReference] = useState<any[]>([]);

  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const { creditApplicationTemplate } = useAppSelector((state) => state.creditApplicationTemplates);
  const { decryptValue } = useDecryption();
  const [isTypedSignatureDisabled, setIsTypedSignatureDisabled] = useState(false);

  useEffect(() => {
    const supplierId = creditApplication?.supplierId;
    if (!supplierId) return;
    fetchCreditApplicationSignatureSettings(supplierId)
      .then((settings) => setIsTypedSignatureDisabled(settings.isTypedSignatureDisabled))
      .catch((error) => console.log(error?.message));
  }, [creditApplication?.supplierId]);

  const resolvedApplication = useMemo(() => {
    if (!creditApplication) return null;
    if (applicationHistoryId) {
      return (
        (creditApplication.applicationUpdateHistory ?? []).find(
          (h: any) => h.id === applicationHistoryId
        ) ?? null
      );
    }
    return creditApplication;
  }, [creditApplication, applicationHistoryId]);

  useEffect(() => {
    if (resolvedApplication?.templateId) {
      dispatch(getCreditApplicationTemplate({ templateId: resolvedApplication.templateId }));
    }
  }, [resolvedApplication?.templateId]);

  const documentTextById = useMemo(() => {
    const map = new Map<string, string>();
    if (creditApplicationTemplate?.id !== resolvedApplication?.templateId) return map;
    creditApplicationTemplate?.steps?.forEach((step) => {
      step.fields?.forEach((field) => {
        field.unGroupedFields?.forEach((f) => {
          if (f.fromField === 'DOCUMENT_FIELD' && f.documentText) map.set(f.id, f.documentText);
        });
        field.groupedFields?.forEach((group) => {
          group.fields?.forEach((f) => {
            if (f.fromField === 'DOCUMENT_FIELD' && f.documentText) map.set(f.id, f.documentText);
          });
        });
      });
    });
    return map;
  }, [creditApplicationTemplate, resolvedApplication?.templateId]);

  useEffect(() => {
    let isMounted = true;

    const decrypt = async () => {
      if (!resolvedApplication) return;

      const { decryptedFields, decryptedBankReferences } = await decryptCreditApplicationData(
        resolvedApplication,
        decryptValue
      );

      if (isMounted) {
        setDecryptedFieldValues(decryptedFields);
        setDecryptedBankReference(decryptedBankReferences);
      }
    };

    decrypt();
    return () => {
      isMounted = false;
    };
  }, [resolvedApplication]);

  const previewFieldValues = useMemo(() => {
    if (documentTextById.size === 0) return decryptedFieldValues;
    return decryptedFieldValues.map((fv) =>
      fv.fromField === 'DOCUMENT_FIELD' && !fv.value && documentTextById.has(fv.id)
        ? { ...fv, value: documentTextById.get(fv.id) }
        : fv
    );
  }, [decryptedFieldValues, documentTextById]);

  const fetchData = async (id: string) => {
    setCreditApplicationId(id);
    await dispatch(getCreditApplication({ creditApplicationId: id }));
  };

  useEffect(() => {
    if (resolvedApplication) {
      const foundSignature = (resolvedApplication.invitedSignatures ?? []).find(
        (sig: any) =>
          sig.email === email && sig.placeHolder.toLowerCase() === role && sig.type === type
      );
      if (!foundSignature) {
        setModalState(ModalState.SUCCESS_ADDING_SIGNATURE);
        setModalDetails({
          modalType: 'Error',
          title: 'Error',
          details: 'You have not been invited to review and sign this credit application.',
        });
      } else {
        if (foundSignature.signature !== '') {
          setModalState(ModalState.SUCCESS_ADDING_SIGNATURE);
          setModalDetails({
            modalType: 'Success',
            title: 'Success',
            details: 'You have already signed this credit application.',
          });
        }
      }
    }
  }, [resolvedApplication, email, role, type]);

  useEffect(() => {
    async function fetchApiData() {
      try {
        const ipRes = await fetch('https://free.freeipapi.com/api/json/');
        const ipData = await ipRes.json();

        setIpData({
          ipAddress: ipData.ipAddress,
          location: `${ipData.cityName}, ${ipData.regionName}, ${ipData.countryName}`,
        });
      } catch (err) {
        console.log(err);
      }
    }

    fetchApiData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramCreditApplicationId = params.get('creditApplicationId');
    if (paramCreditApplicationId) {
      fetchData(paramCreditApplicationId);
    }
    const paramHistoryId = params.get('historyId');
    if (paramHistoryId) {
      setApplicationHistoryId(paramHistoryId);
    }
    const paramEmail = params.get('email');
    if (paramEmail) {
      setEmail(paramEmail);
    }
    const paramRole = params.get('role');
    if (paramRole) {
      setRole(paramRole);
    }
    const paramType = params.get('type');
    if (paramType) {
      setType(paramType);
    }
  }, []);

  const invitedSignature = useMemo(() => {
    if (resolvedApplication && resolvedApplication.invitedSignatures && email && role && type) {
      return resolvedApplication.invitedSignatures.find(
        (sig: any) =>
          sig.email === email && sig.placeHolder.toLowerCase() === role && sig.type === type
      );
    }
    return null;
  }, [resolvedApplication, email, role, type]);

  const invitedSignaturesForEmail = useMemo(() => {
    if (!resolvedApplication?.invitedSignatures || !email) return [];
    return (resolvedApplication.invitedSignatures as any[]).filter(
      (sig) => sig.email.toLowerCase() === email.toLowerCase()
    );
  }, [resolvedApplication, email]);

  const mergedSignaturePageFieldIds = useMemo(() => {
    const allIds = invitedSignaturesForEmail.flatMap((sig: any) =>
      sig.signaturePageFieldIds
        ? sig.signaturePageFieldIds
            .split(',')
            .map((id: string) => id.trim())
            .filter(Boolean)
        : []
    );
    return allIds.length > 0 ? ([...new Set(allIds)] as string[]) : null;
  }, [invitedSignaturesForEmail]);

  const currentInvitedSignature = useMemo(() => {
    return invitedSignaturesForEmail.filter((sig: any) => sig.type === currentSigningType);
  }, [invitedSignaturesForEmail, currentSigningType]);

  useEffect(() => {
    setSubmitError('');
  }, [pendingSignatureData]);

  const unsignedTypesForEmail = useMemo(() => {
    return invitedSignaturesForEmail
      .filter((sig: any) => !(sig.type in pendingSignatureData) && (sig.signature ?? '') === '')
      .map((sig: any) => sig.type as string);
  }, [invitedSignaturesForEmail, pendingSignatureData]);

  const processDynamicFields = async (
    fields: DynamicInvitedSignaturesFields[],
    supplierId: string
  ) => {
    try {
      const processedFields = await Promise.all(
        fields.map(async (field) => {
          let value = field.value;

          if (value instanceof File) {
            const fileType = value.name.split('.').pop() || '';
            try {
              const uploadUrl = await firebaseLargeFileUploadHelper({
                blobUrl: URL.createObjectURL(value),
                fileNameWithExtension: value.name,
                storagePath: `creditApplicationFiles/${supplierId || ''}/${Date.now()}`,
              });

              if (uploadUrl) {
                value = { name: value.name, url: uploadUrl ?? '', fileType };
              } else {
                throw new Error(`Upload failed for file "${value.name}".`);
              }
            } catch (error) {
              console.error(`❌ Failed to upload file "${value.name}":`, error);
              throw new Error(`Upload failed for file "${value.name}".`);
            }
          }

          if (field.isHashed) {
            const secretKey = process.env.REACT_APP_CRYPTO_KEY;
            if (secretKey) {
              value = CryptoJS.AES.encrypt(JSON.stringify(value), secretKey).toString();
            }
          }

          return { ...field, value };
        })
      );

      return processedFields;
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message:
          error.message ||
          'An unexpected error occurred during file upload or submission. Please try again.',
      });
      return null;
    }
  };

  const showModalError = (title: string, details: string) => {
    setModalState(ModalState.SUCCESS_ADDING_SIGNATURE);
    setModalDetails({ modalType: 'Error', title, details });
    setIsLoading(false);
  };

  const showModalSuccess = (title: string, details: string) => {
    setModalState(ModalState.SUCCESS_ADDING_SIGNATURE);
    setModalDetails({ modalType: 'Success', title, details });
  };

  const handleSignNow = (sigType: string) => {
    setCurrentSigningType(sigType);
    setDrawnSignature('');
    setSignatureErrors((prev) => prev.filter((t) => t !== sigType));
    setIsCustomerSignatureModalOpen(true);
  };

  const handleSubmitSignature = (data: { dynamicFields: DynamicInvitedSignaturesFields[] }) => {
    const sigForType = invitedSignaturesForEmail.find(
      (sig: any) => sig.type === currentSigningType
    );
    const entry = {
      dynamicFields: data.dynamicFields,
      signature: drawnSignature,
      email: sigForType?.email ?? email,
      placeHolder: sigForType?.placeHolder ?? '',
      dateSigned: new Date().toISOString(),
      ipAddress: ipdData?.ipAddress,
      location: ipdData?.location,
    };
    setPendingSignatureData((prev) => ({ ...prev, [currentSigningType]: entry }));
    setSignatureErrors((prev) => prev.filter((t) => t !== currentSigningType));
    setIsCustomerSignatureModalOpen(false);
  };

  const handleFinalSubmit = async () => {
    setSubmitError('');

    const missingTypes = invitedSignaturesForEmail.filter(
      (sig: any) => (sig.signature ?? '') === '' && !(sig.type in pendingSignatureData)
    );

    if (missingTypes.length > 0) {
      setSubmitError('Please sign all required signature slots before submitting.');
      setSignatureErrors(missingTypes.map((sig: any) => sig.type as string));
      return;
    }

    try {
      setIsLoading(true);

      const allMatchingSignatures: any[] = [];

      for (const [sigType, pending] of Object.entries(pendingSignatureData)) {
        const processedFields = await processDynamicFields(
          pending.dynamicFields,
          creditApplication?.supplierId ?? ''
        );
        if (!processedFields) {
          setIsLoading(false);
          return;
        }

        const appTitle =
          processedFields.find((field: any) => field.id.includes('title'))?.value || '';

        const signatureData = {
          signature: pending.signature,
          dateSigned: new Date().toISOString(),
          title: appTitle,
          ...(ipdData ? { ipAddress: ipdData.ipAddress, location: ipdData.location } : {}),
        };

        const allInvitedSignatures = resolvedApplication?.invitedSignatures ?? [];
        const matching = (allInvitedSignatures as any[])
          .filter(
            (sig) => sig.email === email && sig.type === sigType && (sig.signature ?? '') === ''
          )
          .map((sig) => {
            const signatureFields = (sig?.fields ?? []).map((field: any) => {
              const existingField = processedFields.find((f) => field.id.includes(f.id));
              if (existingField) {
                return { ...field, value: existingField.value, id: existingField.id };
              }
              return { ...field };
            });
            return { ...sig, ...signatureData, fields: signatureFields };
          });

        allMatchingSignatures.push(...matching);
      }

      if (allMatchingSignatures.length === 0) {
        return showModalError('Error', 'No matching invited signatures found for this user.');
      }

      const results = await dispatch(
        updateCreditApplication({
          creditApplicationId,
          signedInvitedSignature: allMatchingSignatures,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        })
      );

      if (results.type.includes('rejected')) {
        return showModalError(
          'Error Adding Signatures',
          'There was an error submitting your signatures. Please try again.'
        );
      }

      showModalSuccess(
        'Signatures Submitted',
        'Your signatures have been successfully added to the credit application.'
      );
    } catch (error) {
      console.error('Error submitting signatures:', error);
      showModalError('Unexpected Error', 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const title = useMemo(() => {
    const app = resolvedApplication ?? creditApplication;
    if (app) {
      return getCreditApplicationTypeLabel(app.applicationType, {
        long: true,
        formName:
          creditApplicationTemplate?.id === app.templateId ? creditApplicationTemplate?.name : null,
      });
    }
    return ' Application';
  }, [creditApplication, resolvedApplication, creditApplicationTemplate]);

  return (
    <div className="w-full GREY_100-BG min-h-full pb-2 md:pb-4">
      <CreateSignatureModal
        open={isSignatureModalOpen}
        handleClose={() => setSignatureModalOpen(false)}
        addSignature={(sig) => {
          setDrawnSignature(sig);
          setSignatureModalOpen(false);
        }}
        defaultIsManualSign={false}
        onDownloadRealease={() => {}}
        subText="Please sign the credit application to proceed."
        disableTypedSignature={isTypedSignatureDisabled}
      />
      <CustomerSignatureModal
        open={isCustomerSignatureModalOpen}
        handleClose={() => setIsCustomerSignatureModalOpen(false)}
        email={email}
        invitedSignature={currentInvitedSignature}
        signature={drawnSignature}
        onOpenSignature={() => setSignatureModalOpen(true)}
        onSignCreditApplication={handleSubmitSignature}
      />
      <div className="w-full flex items-center justify-center text-lg md:text-xl WHITE-BG font-bold p-2 md:p-5">
        {`Review and Sign ${title}`}
      </div>
      {modalState === ModalState.ADD_SIGNATURE && (
        <div>
          {resolvedApplication && (
            <div className="flex flex-col px-2 md:px-5 py-2 md:py-4 w-full justify-center items-center">
              <div className="w-full md:w-2/3 bg-white p-2 md:p-4 gap-2 md:gap-4">
                <PreviewCreditApplicationData
                  formData={previewFieldValues}
                  bankReference={decryptedBankReference}
                  isSignature={true}
                  signatureInvited={invitedSignature}
                  signing={isLoading}
                  onSignNow={handleSignNow}
                  userSignatureTypes={unsignedTypesForEmail}
                  signaturePageFieldIds={mergedSignaturePageFieldIds}
                  pendingSignedData={pendingSignatureData}
                  signatureErrors={signatureErrors}
                />
                <div className="mt-4 flex flex-col items-end gap-1">
                  {submitError && <p className="text-xs text-red-500">{submitError}</p>}
                  <DillButton
                    label="Submit"
                    variant="primary"
                    onClick={handleFinalSubmit}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                    width="200px"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {modalState === ModalState.SUCCESS_ADDING_SIGNATURE && (
        <div className="flex flex-col items-center w-full justify-center">
          <div className="w-[calc(100%-30px)] md:w-[512px]">
            <JobSavingSuccess
              type={modalDetails.modalType}
              details={modalDetails.details}
              title={modalDetails.title}
            />
          </div>
        </div>
      )}
    </div>
  );
};
